// invest_FE/src/components/topbar/SessionTracker.js
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
import { UserContext } from '../../context/UserContext';
import { CartContext } from '../../context/CartContext'; // Import CartContext
import { NotificationContext } from '../../context/NotificationContext';
import { pingServer, getActiveUserCount } from '../../services/sessionService';
import { apiFetch } from '../../utils/apiClient';
import { BASE_URL, IS_PRERENDERING } from '../../utils/config';

// A unique ID for the browser tab, persists on refresh but not across tabs.
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('appSessionId');
  if (!sessionId) {
    // Use the browser's built-in crypto API for a robust unique ID.
    sessionId = crypto.randomUUID();
    sessionStorage.setItem('appSessionId', sessionId);
  }
  return sessionId;
};

// How often to ping the server to say "I'm still here"
const PING_INTERVAL_MS = 15 * 1000; // 15 seconds

// How often the admin should fetch the latest user count
const ADMIN_FETCH_INTERVAL_MS = 5 * 1000; // 5 seconds

/**
 * A "headless" component that manages user session activity tracking.
 * - Pings the backend periodically to keep the session alive.
 * - If the user is an admin, it fetches the active user count.
 * - It passes the count data down to its children (the display component).
 */
const SessionTracker = ({ children }) => {
  const { user, refreshUser, updateUser } = useContext(UserContext);
  const { fetchCart } = useContext(CartContext); // Get fetchCart from CartContext
  const { addNotification } = useContext(NotificationContext);
  const [activeUsers, setActiveUsers] = useState({ total: 0, registered: 0, guests: 0 });
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const lang = useLanguage();
  // Ref to track the ID of the last inquiry we notified the user about.
  const lastNotifiedInquiryIdRef = useRef(null);

  // useMemo ensures we get the session ID only once per component lifecycle.
  const sessionId = useMemo(() => {
    // This logic should only run in the browser, not during prerendering.
    if (IS_PRERENDERING) {
      return null;
    }
    return getSessionId();
  }, []);

  // --- Main Effect for all client-side session tracking ---
  useEffect(() => {
    // The entire effect, which contains browser-only APIs, should not run during prerendering.
    if (IS_PRERENDERING) {
      return;
    }

    // --- NEW: Use a ref to hold the user object ---
    // This is placed *inside* the main effect to ensure it's only created on the client.
    // We use a ref to ensure the interval callbacks always have the latest user data,
    // preventing pings from being sent with a stale (or null) token.
    const userRef = { current: user };
    // This is a simple way to keep the ref updated without needing a separate useEffect.
    // Since this entire useEffect block re-runs when `user` changes, userRef.current
    // will be correctly reassigned with the latest user object for any new intervals created.
    // Note: We can't use `useRef` here because it would violate rules of hooks.
    // A simple mutable object serves the same purpose within this effect's scope.

    const handleFocus = async () => {
      if (!user.isLoggedIn) return;

      // --- NEW: Exclude payment page from auto-cancellation ---
      // If the user is on the payment selection page, do not run the abandoned order check.
      if (window.location.pathname.includes('/select-payment')) {
        return;
      }

      try {
        const response = await apiFetch(`${BASE_URL}/api/orders/check-abandoned`, {}, user.token);
        if (!response.ok) {
          console.error(`[SessionTracker] Failed to check for abandoned orders, status: ${response.status}`);
          return;
        }

        const { abandonedOrder } = await response.json();

        if (abandonedOrder) {
          const currentSessionId = getSessionId();
          if (abandonedOrder.initiatingSessionId === currentSessionId) {
            // Same tab: auto-cancel the order.
            const cancelResponse = await apiFetch(
              `${BASE_URL}/api/payments/cancel`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ orderId: abandonedOrder._id }),
              },
              user.token
            );

            const cancelResult = await cancelResponse.json();
            if (cancelResponse.ok) {
              addNotification(t('sessionTracker.incompleteOrderCancelled'), 'info');
              if (cancelResult.user) {
                updateUser(cancelResult.user);
              } else {
                refreshUser(); // Fallback
              }
              fetchCart();
            } else {
              addNotification(cancelResult.message || t('sessionTracker.cancelFailed'), 'error');
            }
          } else {
            // Different tab: just notify the user and refresh data.
            addNotification(t('sessionTracker.incompletePaymentOtherTab'), 'warning', 7000);
            refreshUser();
            fetchCart();
          }
        } else {
          // No abandoned order, just refresh data as originally intended.
          refreshUser();
          fetchCart();
        }

        // If the user is a seller, check for new pending inquiries.
        if (user.isSeller) {
          try {
            const inquiryResponse = await apiFetch(`${BASE_URL}/api/inquiries/has-pending`, {}, user.token);
            if (inquiryResponse.ok) {
              const { hasPending, latestInquiryId } = await inquiryResponse.json();
              if (hasPending && latestInquiryId !== lastNotifiedInquiryIdRef.current) {
                addNotification(
                  t('sessionTracker.newLeadsNotification'),
                  'success',
                  null, // duration (null for persistent)
                  null, // icon
                  null, // onClose
                  true, // isPersistent
                  [
                    {
                      text: t('sessionTracker.viewMyInquiries'),
                      onClick: () => navigate(`/${lang}/inquiries`),
                      className: 'notification-action-button-primary',
                    },
                  ]
                );
                lastNotifiedInquiryIdRef.current = latestInquiryId; // Store the ID of the latest inquiry
              }
            }
          } catch (inquiryErr) {
            console.error('Failed to check for pending inquiries on focus:', inquiryErr);
          }
        }
      } catch (err) {
        console.error('[SessionTracker] Error during abandoned order check:', err);
      }
    };

    window.addEventListener('focus', handleFocus);

    // --- Ping Interval (for all users, logged in or guest) ---
    const ping = async () => {
      try {
        // Use the user object from the ref to ensure we always have the latest token.
        const currentUser = userRef.current; // Access the user object from the ref
        // console.log('[SessionTracker] Pinging with token:', currentUser.token ? 'Token exists' : 'No Token');
        await pingServer(sessionId, currentUser.token);
      } catch (err) {
        // This is a background task. Don't show an error to the user, just log it.
        console.error('Session ping failed:', err);
      }
    };

    // Ping immediately on load, then start the interval.
    ping();
    const pingIntervalId = setInterval(ping, PING_INTERVAL_MS);

    // --- Admin Count Fetching Interval ---
    let countIntervalId = null;
    // Only fetch count if the user is an admin and is NOT impersonating someone.
    if (user.isAdmin && !user.impersonatorId) {
      const fetchCount = async () => {
        try {
          // This call requires a valid admin token.
          const counts = await getActiveUserCount(user.token);
          setActiveUsers(counts);
          if (error) setError(null); // Clear previous errors on success.
        } catch (err) {
          console.error('Failed to fetch active user count:', err);
          setError(t('sessionTracker.loadUserCountError'));
        }
      };

      fetchCount();
      countIntervalId = setInterval(fetchCount, ADMIN_FETCH_INTERVAL_MS);
    }

    return () => {
      window.removeEventListener('focus', handleFocus); // Cleanup listener
      clearInterval(pingIntervalId);
      if (countIntervalId) clearInterval(countIntervalId);
    };
  }, [user, sessionId, error, refreshUser, fetchCart, addNotification, updateUser, navigate, t, lang]);

  // Effect to reset the notification shown flag on logout.
  useEffect(() => {
    if (!user.isLoggedIn) {
      lastNotifiedInquiryIdRef.current = null;
    }
  }, [user.isLoggedIn]);

  if (!children) {
    return null;
  }

  // If the user is an admin, clone the child component (TopBar) and pass down the props.
  // Otherwise, just render the child as is.
  return user.isAdmin && !user.impersonatorId ? React.cloneElement(children, { activeUsers, error }) : children;
};

export default SessionTracker;
